import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, useMediaQuery } from '@mui/material';
import CloudOffOutlined from '@mui/icons-material/CloudOffOutlined';
import SmartphoneOutlined from '@mui/icons-material/SmartphoneOutlined';
import CloudDoneOutlined from '@mui/icons-material/CloudDoneOutlined';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';

const STEPS = [
    {
        key: 'offline',
        title: 'Action offline',
        subtitle: 'Capture on device',
        Icon: CloudOffOutlined,
    },
    {
        key: 'local',
        title: 'Stored locally',
        subtitle: 'Queued securely',
        Icon: SmartphoneOutlined,
    },
    {
        key: 'sync',
        title: 'Synced on reconnection',
        subtitle: 'Timestamp locked',
        Icon: CloudDoneOutlined,
    },
];

const CYCLE_MS = 2800;

const OfflineModeSection = () => {
    const isDesktop = useMediaQuery('(min-width:900px)');
    const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const [phase, setPhase] = useState(0);

    useEffect(() => {
        if (reduceMotion) return undefined;
        const id = setInterval(() => {
            setPhase((p) => (p + 1) % 4);
        }, CYCLE_MS);
        return () => clearInterval(id);
    }, [reduceMotion]);

    const p = reduceMotion ? 3 : phase;

    const isComplete = (index) => {
        if (p === 3) return true;
        return index < p;
    };

    const isActive = (index) => !reduceMotion && p < 3 && index === p;

    const connectorFill = (afterIndex) => {
        if (p === 3) return 1;
        if (p > afterIndex) return 1;
        if (p === afterIndex) return 0.55;
        return 0;
    };

    return (
        <Box
            component="section"
            aria-labelledby="offline-mode-heading"
            sx={{
                pt: { xs: 7, md: 7.5, lg: 8 },
                pb: { xs: 6, md: 6.5, lg: 7 },
                background: 'linear-gradient(180deg, #FFFFFF 0%, #F4F8FF 42%, #FAFBFF 100%)',
                borderBottom: '1px solid rgba(0, 102, 255, 0.06)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '18%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 'min(900px, 90vw)',
                    height: '280px',
                    background: 'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(0, 102, 255, 0.06) 0%, transparent 70%)',
                    pointerEvents: 'none',
                },
            }}
        >
            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                <Box sx={{ textAlign: 'center', maxWidth: 820, mx: 'auto', mb: { xs: 4, md: 6 } }}>
                    <Typography
                        component="span"
                        sx={{
                            display: 'inline-block',
                            fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                            fontWeight: 700,
                            letterSpacing: '0.18em',
                            textTransform: 'uppercase',
                            color: '#0066FF',
                            bgcolor: 'rgba(0, 102, 255, 0.1)',
                            px: { xs: 1.75, sm: 2.25 },
                            py: 0.75,
                            borderRadius: 999,
                            mb: 2.5,
                        }}
                    >
                        OFFLINE MODE
                    </Typography>

                    <Typography
                        id="offline-mode-heading"
                        variant="h2"
                        sx={{
                            fontSize: { xs: '1.75rem', sm: '2.125rem', md: '2.5rem' },
                            fontWeight: 700,
                            lineHeight: 1.2,
                            letterSpacing: '-0.03em',
                            color: '#0F172A',
                            mb: 2.5,
                        }}
                    >
                        What happens when there&apos;s no signal
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: { xs: '1rem', sm: '1.0625rem', md: '1.125rem' },
                            lineHeight: 1.75,
                            color: 'rgba(15, 23, 42, 0.78)',
                            textAlign: 'center',
                            mx: 'auto',
                        }}
                    >
                        The app works offline. Check-in, session notes, documentation - all captured locally and synced when
                        connectivity returns. The timestamp is set at creation, on the device. It can&apos;t be backdated.
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isDesktop ? 'row' : 'column',
                        alignItems: isDesktop ? 'stretch' : 'stretch',
                        justifyContent: 'center',
                        maxWidth: 1040,
                        mx: 'auto',
                    }}
                >
                    {STEPS.map((stepItem, index) => (
                        <React.Fragment key={stepItem.key}>
                            <StepCard
                                stepItem={stepItem}
                                index={index}
                                isActive={isActive(index)}
                                isComplete={isComplete(index)}
                                reduceMotion={reduceMotion}
                            />
                            {index < STEPS.length - 1 && (
                                <ProgressConnector
                                    fillLevel={connectorFill(index)}
                                    vertical={!isDesktop}
                                />
                            )}
                        </React.Fragment>
                    ))}
                </Box>
            </Container>
        </Box>
    );
};

function StepCard({ stepItem, index, isActive, isComplete, reduceMotion }) {
    const { title, subtitle, Icon } = stepItem;

    return (
        <Box
            sx={{
                flex: { md: '1 1 0' },
                minWidth: 0,
                ...(!reduceMotion && {
                    animation: 'offlineCardIn 0.7s cubic-bezier(0.22, 1, 0.36, 1) backwards',
                    animationDelay: `${0.12 + index * 0.1}s`,
                    '@keyframes offlineCardIn': {
                        '0%': { opacity: 0, transform: 'translateY(16px)' },
                        '100%': { opacity: 1, transform: 'translateY(0)' },
                    },
                }),
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    borderRadius: '20px',
                    p: { xs: 2.5, md: 3 },
                    minHeight: { xs: 'auto', md: 200 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    background: isActive
                        ? 'linear-gradient(145deg, #FFFFFF 0%, #EEF4FF 55%, #E8EFFF 100%)'
                        : isComplete
                          ? 'linear-gradient(145deg, #FFFFFF 0%, #F0FDF4 45%, #F8FAFC 100%)'
                          : 'linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)',
                    border: isActive
                        ? '1.5px solid rgba(0, 102, 255, 0.35)'
                        : isComplete
                          ? '1.5px solid rgba(34, 197, 94, 0.35)'
                          : '1px solid rgba(15, 23, 42, 0.08)',
                    boxShadow: isActive
                        ? '0 20px 48px rgba(0, 102, 255, 0.14), 0 0 0 1px rgba(255,255,255,0.8) inset'
                        : isComplete
                          ? '0 12px 36px rgba(34, 197, 94, 0.08), 0 8px 24px rgba(15, 23, 42, 0.05)'
                          : '0 8px 28px rgba(15, 23, 42, 0.05)',
                    transform: isActive ? 'translateY(-4px) scale(1.02)' : 'translateY(0) scale(1)',
                    transition: 'transform 0.45s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.45s ease, border-color 0.35s ease, background 0.45s ease',
                    ...(isActive &&
                        !reduceMotion && {
                            animation: 'offlineActivePulse 2.2s ease-in-out infinite',
                            '@keyframes offlineActivePulse': {
                                '0%, 100%': { boxShadow: '0 20px 48px rgba(0, 102, 255, 0.14), 0 0 0 1px rgba(255,255,255,0.8) inset' },
                                '50%': { boxShadow: '0 24px 56px rgba(0, 102, 255, 0.22), 0 0 0 1px rgba(255,255,255,0.9) inset' },
                            },
                        }),
                }}
            >
                {isComplete && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            color: '#22C55E',
                            ...(!reduceMotion && {
                                animation: 'checkPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                '@keyframes checkPop': {
                                    '0%': { opacity: 0, transform: 'scale(0.3)' },
                                    '100%': { opacity: 1, transform: 'scale(1)' },
                                },
                            }),
                        }}
                    >
                        <CheckCircleRounded sx={{ fontSize: 26 }} />
                    </Box>
                )}

                <Box
                    sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        background: isActive
                            ? 'linear-gradient(135deg, rgba(0, 102, 255, 0.18) 0%, rgba(99, 102, 241, 0.14) 100%)'
                            : isComplete
                              ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(16, 185, 129, 0.1) 100%)'
                              : 'linear-gradient(135deg, rgba(15, 23, 42, 0.06) 0%, rgba(15, 23, 42, 0.03) 100%)',
                        color: isActive ? '#0066FF' : isComplete ? '#15803D' : 'rgba(15, 23, 42, 0.45)',
                        transition: 'background 0.35s ease, color 0.35s ease, transform 0.35s ease',
                        ...(isActive &&
                            !reduceMotion && {
                                animation: 'iconPulse 2s ease-in-out infinite',
                                '@keyframes iconPulse': {
                                    '0%, 100%': { transform: 'scale(1)' },
                                    '50%': { transform: 'scale(1.06)' },
                                },
                            }),
                    }}
                >
                    <Icon sx={{ fontSize: 28 }} />
                </Box>

                <Typography
                    sx={{
                        fontSize: { xs: '1rem', md: '1.0625rem' },
                        fontWeight: 700,
                        color: '#0F172A',
                        letterSpacing: '-0.02em',
                        mb: 0.5,
                        lineHeight: 1.35,
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    sx={{
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                        color: 'rgba(15, 23, 42, 0.55)',
                        lineHeight: 1.5,
                    }}
                >
                    {subtitle}
                </Typography>

                <Box
                    sx={{
                        mt: 'auto',
                        pt: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.75,
                    }}
                >
                    {[0, 1, 2].map((dot) => (
                        <Box
                            key={dot}
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                bgcolor:
                                    isActive && dot === 1
                                        ? 'rgba(0, 102, 255, 0.85)'
                                        : 'rgba(15, 23, 42, 0.12)',
                                transition: 'background 0.3s ease, transform 0.3s ease',
                                ...(isActive &&
                                    !reduceMotion &&
                                    dot === 1 && {
                                        animation: 'dotPulse 1.2s ease-in-out infinite',
                                        '@keyframes dotPulse': {
                                            '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                                            '50%': { transform: 'scale(1.35)', opacity: 0.7 },
                                        },
                                    }),
                            }}
                        />
                    ))}
                </Box>
            </Box>
        </Box>
    );
}

function ProgressConnector({ fillLevel, vertical }) {
    const level = Math.min(1, Math.max(0, fillLevel));
    return (
        <Box
            sx={{
                flex: vertical ? 'none' : '0 0 44px',
                width: vertical ? 5 : 'auto',
                height: vertical ? 40 : 5,
                minWidth: vertical ? 5 : 0,
                alignSelf: vertical ? 'center' : 'center',
                my: vertical ? 1.25 : 0,
                mx: vertical ? 'auto' : 0,
                position: 'relative',
                borderRadius: 999,
                background: vertical
                    ? 'linear-gradient(180deg, rgba(0, 102, 255, 0.1) 0%, rgba(99, 102, 241, 0.14) 100%)'
                    : 'linear-gradient(90deg, rgba(0, 102, 255, 0.1) 0%, rgba(99, 102, 241, 0.14) 100%)',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    ...(vertical
                        ? {
                              left: 0,
                              right: 0,
                              top: 0,
                              height: `${level * 100}%`,
                              width: '100%',
                          }
                        : {
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: `${level * 100}%`,
                          }),
                    borderRadius: 999,
                    background: vertical
                        ? 'linear-gradient(180deg, #0066FF 0%, #6366F1 100%)'
                        : 'linear-gradient(90deg, #0066FF 0%, #6366F1 100%)',
                    transition: 'width 0.7s cubic-bezier(0.22, 1, 0.36, 1), height 0.7s cubic-bezier(0.22, 1, 0.36, 1)',
                    boxShadow: '0 0 14px rgba(0, 102, 255, 0.28)',
                }}
            />
        </Box>
    );
}

export default OfflineModeSection;
